import {
	hasLookupMembership,
	hasNoAuth0Login,
	NEW_MEMBER_CONTACT_ACTION,
} from 'types'
import { SEATOW_PHONE } from 'config'

const RENEWAL_MODAL = {
	heading: 'Email Address Already on File',
	prompt: `The email provided is already associated with a Sea Tow membership(s),
    would you instead like to log in and renew your existing membership?
    If you would like to create a new account with this email or add a new
    membership to your existing account, please contact Sea Tow for assistance.`,
	confirmLabel: 'Log In and Renew',
	cancelLabel: 'Cancel',
}

// For accounts without a membership, we don't want to force a sign-in, so
// "Proceed as Guest" is the confirm button and "Sign In" sits where cancel
// normally would.
const SIGN_IN_MODAL = {
	heading: 'Existing Login Found',
	prompt:
		'A Sea Tow login with your email exists. We recommend you sign in to continue joining.',
	confirmLabel: 'Proceed as Guest',
	cancelLabel: 'Sign In',
	preventClose: true,
}

// No guest path on an email-only match; sign in or exit.
const EMAIL_ONLY_MODAL = {
	heading: 'Sea Tow Account Found',
	prompt:
		'It looks like you already have a Sea Tow login in the mobile app. To continue, you must either log in or exit and join via the Sea Tow app.',
	confirmLabel: 'Login',
	cancelLabel: "I'll Join via the App",
	preventClose: true,
}

// Membership on file but no Auth0 login: they joined before and never
// registered online. Send them to the login page to create a login (the
// membership associates by email) rather than start a second application.
const CREATE_LOGIN_MODAL = {
	heading: 'Membership Found',
	prompt:
		'It looks like you have a Sea Tow membership, but no online login yet. Create a login to manage or renew your membership.',
	confirmLabel: 'Create a Login',
	cancelLabel: 'Cancel',
}

// Email matched but phone didn't, and there is no Auth0 login to send them
// to. No write, no Login CTA — dismiss so they can fix phone and/or email.
const EMAIL_IN_USE_MODAL = {
	heading: 'Email Already In Use',
	prompt: `This email is already on file with Sea Tow, but we couldn't verify it with the phone number you entered. Please check your email and phone, or contact Sea Tow at ${SEATOW_PHONE} if you need help.`,
	confirmLabel: 'Close',
}

// Email matched but phone didn't — no guest path; sign in or bail out.
const confirmEmailOnlyAction = async (confirmModal) => {
	const wantsToLogin = await confirmModal(EMAIL_ONLY_MODAL)
	return wantsToLogin
		? NEW_MEMBER_CONTACT_ACTION.SIGN_IN
		: NEW_MEMBER_CONTACT_ACTION.EXIT_TO_MARKETING
}

// No Auth0 login and no verified phone match — block continue without
// promising a login they don't have.
const confirmEmailInUseAction = async (confirmModal) => {
	await confirmModal(EMAIL_IN_USE_MODAL)
	return NEW_MEMBER_CONTACT_ACTION.CANCEL
}

// Decides what the contact-step submit handler should do based on the existing
// contact, whether a real membership is on file, and whether the email has an
// Auth0 login. See `hasLookupMembership` / `hasNoAuth0Login` for missing-flag
// fallback semantics.
const getNewMemberContactAction = async ({
	existingContact,
	skipModals,
	confirmModal,
}) => {
	const { contact_exists, email_exists } = existingContact ?? {}
	const hasNoLogin = hasNoAuth0Login(existingContact)
	const isRealMember = hasLookupMembership(existingContact)

	// Signed-in users have already passed the modal gates, so just update.
	if (skipModals) return NEW_MEMBER_CONTACT_ACTION.UPDATE

	// A real membership on file means they've joined before. With a login they
	// renew; without one they need to register first — but only after phone
	// verifies the contact. An email-only match must not get a Login / Create
	// Login CTA. Cart shells (`membership_state: 'cart'`) continue / PATCH.
	if (isRealMember) {
		if (hasNoLogin) {
			if (contact_exists) {
				const confirmed = await confirmModal(CREATE_LOGIN_MODAL)
				return confirmed
					? NEW_MEMBER_CONTACT_ACTION.CREATE_LOGIN
					: NEW_MEMBER_CONTACT_ACTION.CANCEL
			}
			return confirmEmailInUseAction(confirmModal)
		}

		const confirmed = await confirmModal(RENEWAL_MODAL)
		return confirmed
			? NEW_MEMBER_CONTACT_ACTION.RENEW
			: NEW_MEMBER_CONTACT_ACTION.CANCEL
	}

	// No membership and no login: resume an abandoned application only when
	// email and phone both match. Email-only matches withhold IDs on the
	// backend — block without a Login CTA (they have no Auth0 account).
	if (hasNoLogin) {
		if (contact_exists) return NEW_MEMBER_CONTACT_ACTION.UPDATE
		if (email_exists) return confirmEmailInUseAction(confirmModal)
		return NEW_MEMBER_CONTACT_ACTION.CREATE
	}

	// Has a login. Email + phone matched a single null-member contact, guest
	// update or sign in are both fine.
	if (contact_exists) {
		const proceedAsGuest = await confirmModal(SIGN_IN_MODAL)
		return proceedAsGuest
			? NEW_MEMBER_CONTACT_ACTION.UPDATE
			: NEW_MEMBER_CONTACT_ACTION.SIGN_IN
	}

	if (email_exists) return confirmEmailOnlyAction(confirmModal)

	return NEW_MEMBER_CONTACT_ACTION.CREATE
}

export default getNewMemberContactAction
